import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, signal, computed, OnChanges, NO_ERRORS_SCHEMA } from '@angular/core';
import { AccessLevelType, AccessLevelTypeDescription, FundamentoLegal, Patriarca } from '../../../../../models/edocs.model';
import { FundamentosFilter, PatriarcasFilter } from '../../default/fod-default.pipe';
import { Enums } from '../../../../../shared/enums';
import { EDocsService } from '../../../../../services/edocs.service';
import { IBaseOption } from '../../../../../models/base.model';
import { Utils } from '../../../../../shared/utils';
import { ToastrService } from 'ngx-toastr';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { OrganogramaService } from '../../../../../services/organograma.service';
import { ConfigSchemaSectionAccessLevel } from '../../../../../models/config-schema.model';
import { IMPORTS } from './_imports';

@Component({
    selector: 'access-level',
    templateUrl: './access-level.component.html',
    styleUrl: './access-level.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class AccessLevelComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('fundamentacaoLegalRef') fundamentacaoLegalRef: ElementRef;
    // #endregion

    // #region [Type props]
    AccessLevelType = AccessLevelType;
    AccessLevelFilterType = Enums.AccessLevelFilterType;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [props]
    accessLevelTypeOptions: IBaseOption[] = [];
    patriarcas: Patriarca[] = [];
    fundamentoId = '';
    fundamentoNome: string = null;
    fundamentos: FundamentoLegal[] = [];
    // #endregion

    // #region [signals]
    $model = signal(null as ConfigSchemaSectionAccessLevel);
    $flowDefinitionOrganizationId = signal(null as string);
    $isReadOnlyMode = signal(false);
    $patriarcaId = signal('');
    $patriarcaDisplay = signal('');
    $patriarcaNome = signal(null as string);
    $selectablePatriarcas = signal(null as Patriarca[]);
    $fundamentoDisplay = signal('');
    $fundamentoDescricao = signal(null as string);
    $fundamentosNomes = signal(null as string);
    $fundamentoFilterType = signal(null as Enums.AccessLevelFilterType);
    $selectableFundamentos = signal([] as FundamentoLegal[]);
    $selectedFundamentos = signal([] as IBaseOption[]);
    // #endregion

    // #region [computeds]
    $isFundamentoDisplayDisabled = computed(() =>
        this.$isReadOnlyMode()
        || Utils.isNullOrEmpty(this.$fundamentoFilterType())
        || (
            this.$fundamentoFilterType() == Enums.AccessLevelFilterType.Patriarch
            && Utils.isNullOrEmpty(this.$patriarcaId())
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: ConfigSchemaSectionAccessLevel;
    @Input() _flowDefinitionOrganizationId: string;
    @Input() _isReadOnlyMode: boolean;
    @Output() _modelChange = new EventEmitter<ConfigSchemaSectionAccessLevel>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private organogramaService: OrganogramaService,
        private edocsService: EDocsService,
        private fundamentosFilter: FundamentosFilter,
        private patriarcasFilter: PatriarcasFilter
    ) {
        for (let item in AccessLevelType) {
            // **X**: o endpoint de encaminhamento da v2 exige parâmetros extras em "classificacaoInformacao"
            // que a v1 não exigia; "Classified" fica pra depois
            if (!Number.isNaN(Number.parseInt(item)) && +item != AccessLevelType.Classified) {
                this.accessLevelTypeOptions.push({
                    value: +item,
                    description: AccessLevelTypeDescription.get(+item)
                });
            }
        }
    }

    ngOnInit() {
        let interval = setInterval(async () => {
            if (this.$model() != null) {
                clearInterval(interval);

                this.initLegalReasoningsInfo();

                const response_Patriarcas = await this.edocsService.getPatriarcas();

                if (!response_Patriarcas.isSuccess) {
                    this.toastr.error(response_Patriarcas.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                this.patriarcas = response_Patriarcas.data;
                this.patriarcas.sort((a, b) => {
                    let first = a.sigla || a.nome || '';
                    let second = b.sigla || b.nome || '';
                    return first.localeCompare(second);
                });
                Utils.setSignal(this.$selectablePatriarcas, this.patriarcas);
            }
        }, 100);
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.initLegalReasoningsInfo();
        this.$flowDefinitionOrganizationId.set(this._flowDefinitionOrganizationId);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    fundamentoDisplayChange() {
        this.$selectableFundamentos.set(this.fundamentosFilter.transform(this.fundamentos, this.$fundamentoDisplay()));
    }

    fundamentoIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.fundamentoId = event.option.value.id;
        }

        let fundamento = this.fundamentos.find(x => x.id == this.fundamentoId);
        this.fundamentoNome = `${fundamento.nivelAcesso} - ${fundamento.nome}`;
        this.$fundamentoDescricao.set(`${fundamento.nome} - ${fundamento.descricao}`);
    }

    displayFundamentoId(option): string {
        return option != null && option != ''
            ? `${option.nivelAcesso.toUpperCase()} - ${option.nome.toUpperCase()}`
            : '';
    }

    addFundamento(event) {
        if (this.$isFundamentoDisplayDisabled()) return;

        if (
            // caso o Nível de Acesso selecionado seja Sigiloso ou Restrito
            this.shouldFilterFundamentos()
            // caso a opção já não esteja incluída
            && this.$selectedFundamentos().find(x => x.value == this.fundamentoId) == null
            // caso a opção selecionada não esteja vazia
            && Utils.isNotNullOrEmpty(this.fundamentoId)
            && Utils.isNotNullOrEmpty(this.fundamentoNome)
        ) {
            Utils.updSignal(this.$selectedFundamentos, x => {
                x.push({
                    value: this.fundamentoId,
                    description: this.fundamentoNome
                });
                return x;
            });

            // atualiza conteúdo do tooltip
            this.$fundamentosNomes.set('');
            this.$selectedFundamentos().forEach(x => this.$fundamentosNomes.set(this.$fundamentosNomes() + `${x.description}; `));
            this.$fundamentosNomes.set(this.$fundamentosNomes().split('; ').slice(0 ,-1).join('; '));
            this.$fundamentoDescricao.set('');

            this.$fundamentoDisplay.set('');
            Utils.setSignal(this.$selectableFundamentos, this.fundamentos);
        }

        this.deselectOptions(event);

        this.resolveLegalReasoningsInfo();
    }

    removeFundamento(event) {
        let selectedOptionsValues = Array.from((this.fundamentacaoLegalRef.nativeElement as HTMLSelectElement).options).filter(x => x.selected).map(x => x.value);
        selectedOptionsValues.forEach(item => {
            let idx = this.$selectedFundamentos().findIndex(x => item.includes(x.value));
            Utils.updSignal(this.$selectedFundamentos, x => {
                x.splice(idx, 1);
                return x;
            });
        });

        this.deselectOptions(event);

        this.resolveLegalReasoningsInfo();
    }

    shouldFilterFundamentos(): boolean {
        return [AccessLevelType.Confidential, AccessLevelType.Classified].includes(+this.$model().id);
    }

    async fundamentoFilterTypeChange(event) {
        this.$fundamentoFilterType.set(event);

        if (this.$fundamentoFilterType() == Enums.AccessLevelFilterType.Citizen) {
            const response_Fundamentos = await this.edocsService.getFundamentosLegaisCidadao();

            if (!response_Fundamentos.isSuccess) {
                this.toastr.error(response_Fundamentos.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.fundamentos = response_Fundamentos.data;
            this.fundamentos.sort((a, b) => a.ordenacao - b.ordenacao);
            this.filterFundamentosSigilosos();
            Utils.setSignal(this.$selectableFundamentos, this.fundamentos);

            this.clearPatriarca();
            this.fundamentoId = null;
        }
    }

    patriarcaDisplayChange() {
        this.$selectablePatriarcas.set(this.patriarcasFilter.transform(this.patriarcas, this.$patriarcaDisplay()));
    }

    async patriarcaIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.$patriarcaId.set(event.option.value.id);
        }

        if (this.$patriarcaId() != '') {
            const response_Fundamentos = await this.edocsService.getFundamentosLegaisPatriarca(this.$patriarcaId());

            if (!response_Fundamentos.isSuccess) {
                this.toastr.error(response_Fundamentos.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.fundamentos = response_Fundamentos.data;
            this.fundamentos.sort((a, b) => a.ordenacao - b.ordenacao);
            this.filterFundamentosSigilosos();
            Utils.setSignal(this.$selectableFundamentos, this.fundamentos);
            this.$patriarcaNome.set(`${event.option.value.sigla} - ${event.option.value.nome}`.toUpperCase());
        } else {
            this.$patriarcaNome.set(null);
        }
    }

    displayPatriarcaId(option): string {
        return option != null && option != ''
            ? `${option.sigla} - ${option.nome.toUpperCase()}`
            : '';
    }

    clearPatriarca() {
        this.$patriarcaId.set('');
        this.$patriarcaDisplay.set('');
        Utils.setSignal(this.$selectablePatriarcas, this.patriarcas);
    }

    deselectOptions(event) {
        setTimeout(() => event.value = null, 1);
    }

    accessLevelIdChange(event) {
        Utils.updSignal(this.$model, x => {
            x.id = event;
            return x;
        });

        this.$selectedFundamentos.set([]);
        this.resolveLegalReasoningsInfo();
        this.$fundamentosNomes.set('');
        this.fundamentoId = '';
        this.$fundamentoDescricao.set('');
        this.clearPatriarca();
        this.$fundamentoFilterType.set(null);

        if (this.shouldFilterFundamentos()) {
            this.filterFundamentosSigilosos();
        }

        Utils.updSignal(this.$model, x => {
            x.name = this.accessLevelTypeOptions.find(x => x.value == this.$model().id).description;
            return x;
        });

        this._modelChange.emit(this.$model());
    }

    initLegalReasoningsInfo() {
        if (this.$model().legalReasoningsIds?.length > 0) {
            this.$selectedFundamentos.set([]);

            for (let i = 0; i < this.$model().legalReasoningsIds.length; i++) {
                Utils.updSignal(this.$selectedFundamentos, x => {
                    x.push({
                        value: this.$model().legalReasoningsIds[i],
                        description: this.$model().legalReasoningsNames[i]
                    });
                    return x;
                });
            }
        }
    }

    // #region [===== private methods =====]
    // #endregion

    private filterFundamentosSigilosos() {
        if (AccessLevelType.Confidential == +this.$model().id) {
            this.fundamentos = this.fundamentos.filter(x => x.nivelAcesso?.toUpperCase() == "SIGILOSO");
            // motivo: ver **X**
        //} else if (AccessLevelType.Classified == +this.$model().id) {
        //    this.fundamentos = this.fundamentos.filter(x => x.nivelAcesso?.toUpperCase() == "CLASSIFICADO");
        }
    }

    private resolveLegalReasoningsInfo() {
        Utils.updSignal(this.$model, x => {
            x.legalReasoningsIds = [];
            x.legalReasoningsNames = [];
            return x;
        });

        if (this.$selectedFundamentos().length > 0) {
            this.$selectedFundamentos().forEach(y => {
                Utils.updSignal(this.$model, x => {
                    x.legalReasoningsIds.push(y.value);
                    x.legalReasoningsNames.push(y.description);
                    return x;
                });
            });
        }

        this._modelChange.emit(this.$model());
    }
}
